import React from 'react';
import LogoSvg from '../../assets/images/logo/logo-green-blue.svg';
import { cn } from '../../utils';
import Row from '../UI/Containers/Row';
import { LucideIcon, Menu, NotepadText } from 'lucide-react';
import Column from '../UI/Containers/Column';

import { SvgMaskIcon } from './SvgMaskIcon';
import { useTranslation } from 'react-i18next';
import { useUserRoleType } from '../../pages/Account/hooks/useUserRoleType';
import { useHistory } from 'react-router';
import MenuIcon from '../../assets/icons/menu/menu.svg';
import AnimatedIcon from '../UI/AnimatedIcon';
import Visibility from '../UI/Containers/Visibility';
import MenuCloseIcon from '../../assets/icons/menu/menu_close.svg';
import { MediumText, SubtitleText } from '../UI/Typography/PageHeader';

const SidebarView = ({
  isDrawarOpen,
  routes,
  onDrawerClose,
}: {
  isDrawarOpen: boolean;
  routes: any[];
  onDrawerClose?: () => void;
}) => {
  const { t } = useTranslation();
  const { currentRole, roles, isMurabahaRepresentative } = useUserRoleType();
  const pathname = window.location.pathname;
  const history = useHistory();
  return (
    <section className="w-full h-full bg-white pt-2 overflow-hidden min-h-screen col gap-0">
      {/* Logo */}
      <div
        className={cn(
          'flex items-center justify-center w-full border-b min-h-[63px] sm:min-h-[67px] border-border2Color'
        )}
      >
        <img
          src={LogoSvg}
          alt="logo"
          className={cn(
            'object-contain animated h-auto',
            isDrawarOpen ? 'w-16' : 'w-10'
          )}
        />
      </div>

      {/* Nav Items */}
      <Column className={'p-5 hideScroll min-h-[85%]'}>
        {routes.map((route, key) => {
          if (route.hiddenInSidebar) return null;
          if (isMurabahaRepresentative) {
            if (!route.roles?.some((r) => roles.includes(r))) return null;
          } else {
            if (!route.roles?.includes(currentRole)) return null;
          }
          return (
            <SidebarNavItem
              key={key}
              icon={route.newIcon}
              label={t(`components.sidebar.${route.name}`)}
              isActive={pathname.startsWith(route.path)}
              onClick={() => history.push(route.path)}
              //
              isDrawarOpen={isDrawarOpen}
            >
              {route.children?.map((childRoute, childKey) => {
                return (
                  <SidebarNavItem
                    key={childKey}
                    icon={childRoute.newIcon}
                    label={t(`components.sidebar.${childRoute.name}`)}
                    isActive={pathname.startsWith(childRoute.path)}
                    onClick={() => history.push(childRoute.path)}
                    //
                    isDrawarOpen={isDrawarOpen}
                    isChildMenu
                  />
                );
              })}
            </SidebarNavItem>
          );
        })}
      </Column>

      {/* Menu close icon */}
      <Visibility
        isVisible={!isDrawarOpen}
        replacement={
          <SubtitleText
            text={t('common.copyrights')}
            className="text-center text-xs text-hintText self-center"
          />
        }
      >
        <button onClick={onDrawerClose} className="btnEffect p-5 w-fit">
          <SvgMaskIcon className="bg-secondary" src={MenuCloseIcon} />
        </button>
      </Visibility>
    </section>
  );
};

export default SidebarView;

const SidebarNavItem = ({
  icon,
  label,
  isActive = false,
  className,
  children,
  onClick,
  isDrawarOpen = false,
  isChildMenu = false,
}: {
  icon: string;
  label: string;
  isActive?: boolean;
  className?: string;
  //
  children?: React.ReactNode;
  onClick?: () => void;
  isDrawarOpen?: boolean;
  isChildMenu?: boolean;
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  // Case (Children main item): Menu is open, has children, and is active, then apply bg-secondary
  const isMainMenuOpnAndActive = isDrawarOpen && children && isActive;
  return (
    //  border-b last:border-b-0
    <Column
      className={cn(
        'gap-0',

        !isDrawarOpen && 'border-b last:border-b-0 py-1.5 first:pt-0'
      )}
    >
      <Row
        onClick={() => {
          if (children) {
            setIsOpen(!isOpen);
          } else {
            onClick?.();
          }
        }}
        className={cn(
          'gap-5 items-center w-full p-3 rounded-lg cursor-pointer text-sm text-labelText font-semibold animated',
          isActive && 'text-white bg-primary',
          !isDrawarOpen && 'p-1 centerDiv',
          // Case (Icon only sidebar): Drawer is closed, has children, and is active, then apply bg-secondary
          !isDrawarOpen && children && isActive && 'bg-secondary',

          // Case (Children main item): Menu is open, has children, and is active, then apply bg-secondary
          isMainMenuOpnAndActive && 'bg-secondaryBg text-secondary',
          className
        )}
      >
        <SvgMaskIcon
          src={icon || MenuIcon}
          width={isDrawarOpen ? 18 : 22}
          height={isDrawarOpen ? 18 : 22}
          className={
            isMainMenuOpnAndActive ? 'bg-secondary' : isActive && 'bg-white'
          }
        />

        {isDrawarOpen && <span className="truncate">{label}</span>}

        {children && isDrawarOpen && (
          <AnimatedIcon
            isOpen={isOpen}
            className="ml-auto"
            iconClassName={cn(
              'w-5 h-5',
              isActive && 'text-white',
              isMainMenuOpnAndActive && 'text-secondary'
            )}
          />
        )}
      </Row>

      {children && (
        <Column
          className={cn(
            'gap-0',
            //  isOpen && isDrawarOpen ? 'p-3' : 'px-0.5 pt-2'
            isOpen && (isDrawarOpen ? 'p-3' : 'px-[1px] pt-2')
          )}
          style={{
            maxHeight: isOpen ? '100%' : '0',
            opacity: isOpen ? 1 : 0,
            transform: isOpen ? 'scaleY(1)' : 'scaleY(0)',
            transformOrigin: 'top',
            // Use transition for smooth animation
            transition: 'all 0.2s ease-in-out',
          }}
        >
          {children}
        </Column>
      )}
    </Column>
  );
};
